import { toKebabCase } from "~/utils/string";

/**
 * A composable function to determine the user's access rights for a given route.
 *
 * @param route - (Optional) The route to check permissions for.  If not provided, the current route from `useRoute()` will be used.
 * @returns A computed ref containing the user's access right (e.g., 'read', 'write', 'none') or `undefined` if the user is not logged in or permissions are not available.
 */
export const usePermissions = (route?: string) => {
  const { user, session } = useUserSession();
  const permissions = session.value?.permissions;

  return computed(() => {
    // Determine the route to use for permission checking.
    const routeToBeUsed = route ? route : useRoute().path;
    // Normalize path: if root, check 'curtailment', otherwise strip the leading slash
    const normalizedPath = routeToBeUsed === "/" ? "curtailment" : routeToBeUsed.substring(1);

    // If the user or permissions are not available, return undefined.
    if (!user.value || !permissions) return undefined;

    // Find the permission that matches the kebab-cased menu name and return the access right as array of string.
    const permissionsAccess = permissions
      .filter((permission) => toKebabCase(permission.menu) === normalizedPath)
      .map((permission) => permission.permission);

    // Should be safer if using `.includes("view")` to restrict unintentionally wrong setup access from backend
    return permissionsAccess;
  });
};
